import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { JobNames } from "../../framework.core/JobNames";
import { useEmailJobStatus } from "../../hooks/useEmailJobStatus";
import useExternalScreenFilter from "../../hooks/useExternalScreenFilter";
import useSetEditedId from "../../hooks/useSetEditedId";
import { buildActionToast, buildDuplicateToast, buildProcessedRecordToast, processedRecords } from "../../ui/toast";
import { NavigationType, openNewPage } from "../../util/navigationHelpers";
import excelImg from '../../img/memo_doc_excel.svg';
import pdfImg from '../../img/memo_doc_pdf.svg';
import wordImg from '../../img/memo_doc_word.svg';
import pngImg from '../../img/memo_doc_png.svg';
import { AI_CENTER_CONTACT_SUPPORT, isDropZoneOff, useAiCenterFlags } from '../../aiCenterFlags';
import useGlobalOkDialog from "../../hooks/useGlobalOkDialog";
import aiDropzoneDefault from '../../img/ai-drop-zone-default.svg';
import aiDropzoneHover from '../../img/ai-drop-zone-hover.svg';

/**
 * 우측 하단 AI 플로팅 버튼 + Drag & Drop zone 카드.
 *
 * - AI 아이콘 클릭 → 아이콘 위로 카드가 펼쳐진다 (재클릭/× 로 접힘)
 * - 카드에 .eml/.msg 파일을 드래그 앤 드랍(또는 클릭 선택) → 업로드 시작
 * - 이메일 1건 = 카드 하단에 항목 1줄:  ✉ 파일명 + 상태 텍스트(Uploading/Classifying/…)
 *   (진행 중 스피너 / 완료 체크 아이콘이 텍스트 왼쪽에 붙는다 — StageLabel)
 * - 분류가 끝나면 그 아래 문서(Category)들이 들여쓰기로 붙고,
 *   각 문서는 [Parsing→Parsed→Doing→Done] 4단계 진행 바를 갖는다 (각자 독립 진행)
 * - Unknown 클래스 문서는 카드에 표시하지 않는다 (백엔드에서도 Skipped로 제외됨)
 * - 의존성 게이트에 막혀 대기 중(backend가 holding 플래그 통지)이면
 *   해당 문서의 진행 바가 앰버 톤 + 그라데이션 shimmer 로 "Holding" 표시된다
 * - 액션 종료(documentCompleted)마다 onToast로 토스트 발행 (App에서 렌더)
 * - 한 이메일의 모든 작업이 성공(Done)하면 완료 표시를 5초 보여준 뒤 부드럽게 사라진다
 */

const DOC_STEPS = ["Parsing", "Parsed", "Doing", "Done"];

/* 의존성 대기(Holding) 판정 — 백엔드가 게이트(ActionDependencies) 평가 결과를
 * documentUpdated 이벤트의 holding 플래그로 내려준다. 어떤 카테고리가 선행인지는
 * 백엔드 설정만 알면 되므로, 설정이 바뀌거나 추가돼도 프론트 수정은 필요 없다.
 * status 가드는 이후 전이(Doing/Done) 뒤에 남은 옛 플래그를 무시하기 위한 안전장치. */
function isHeldByDependency(d: any) {
  return d.holding === true && d.status === "Standardized";
}

/** 문서 상태 → 4단계 진행 표기 */
function docStage(d: any) {
  switch (d.status) {
    case "AwaitingSelection":
    case "Queued":
    case "Standardizing":
      return { current: 0, working: true, label: "Parsing" };
    case "Standardized":
      return { current: 1, working: false, label: "Parsed" }; // 액션 대기(선행 대기 포함)
    case "ActionRunning":
      return { current: 2, working: true, label: "Doing" };
    case "Completed":
      return { current: 3, done: true, label: "Done" };
    case "Failed":
      return { error: true, label: "Failed", title: d.error };
    case "Skipped":
      return { skipped: true, label: "Skipped" };
    default:
      return { current: 0, working: false, label: d.status };
  }
}

/** 이메일(job) phase → 상태 표기 (이메일 줄은 StageLabel로 텍스트+아이콘만 표시) */
function emailStage(j: any) {
  switch (j.phase) {
    case "uploading":  return { current: 0, working: true, label: "Uploading" };
    case "uploaded":   return { current: 1, working: true, label: "Uploaded" };
    case "classifying": {
      const n = j.fileCount ?? 0;
      return { current: 1, working: true, label: n > 0 ? `Classifying (${n} file(s))` : "Classifying" };
    }
    case "classified": return { current: 2, working: true, label: "Classified" };
    case "processing": return { current: 2, working: true, label: "Processing" };
    case "resuming":   return { current: 2, working: true, label: "Resuming" };
    case "done":           return { current: 3, done: true, label: "Done" };
    case "doneWithErrors": return { current: 3, done: true, warn: true, label: "Done" };
    case "duplicate":      return { blocked: true, label: "Already processed", title: j.message };
    case "failed":
    case "error":
      return { error: true, label: "Failed", title: j.message };
    default:
      return { current: 0, working: false, label: j.phase };
  }
}

/**
 * 화물 추적 스타일의 컴팩트 단계 표시 — 점 4개 + 현재 단계 라벨만 보여준다.
 * (문서 줄 전용 — 이메일 줄은 StageLabel 사용)
 *
 * 시각적 위계(진행 중인 점이 가장 도드라지도록):
 *   지난 단계  = 연한 accent 점 / 연한 연결선
 *   현재 단계  = 진한 accent + 확대 + 헤일로 + 확산 링 + accent 라벨
 *   남은 단계  = 회색 외곽선 점
 *   전체 완료  = 지난 점들도 진한 accent 로 복귀(.is-done)
 *   의존성 대기 = 앰버 톤으로 전환 — 지난 점은 연한 앰버, 현재(Parsed) 점·연결선·
 *                "Holding" 라벨에 앰버 그라데이션 shimmer 가 흐른다(.hold/.holdnow)
 */
function StepProgress({ steps, stage }: { steps: any; stage: any }) {
  if (stage.error)
    return <span className="aidz-stage err" title={stage.title || ""}>✕ {stage.label}</span>;
  if (stage.skipped)
    return <span className="aidz-stage skip">Skipped</span>;

  const filled = (i: any) =>
    stage.done ? true : stage.working ? i < stage.current : i <= stage.current;
  const pulse = (i: any) => !stage.done && stage.working && i === stage.current;
  const hold = stage.holding ? " hold" : "";

  return (
    <span className={`aidz-progress${stage.done ? " is-done" : ""}`}
          title={stage.holding
            ? "Holding — waiting for a prerequisite workflow to finish"
            : `${stage.label} · ${steps.join(" → ")}`}>
      <span className="aidz-dots" aria-hidden="true">
        {steps.map((s: any, i: any) => (
          <span key={s} className="aidz-seg">
            {i > 0 && (
              <span className={`aidz-line${filled(i) ? " on" : ""}${pulse(i) ? " active" : ""}${hold}`} />
            )}
            <span className={`aidz-dot${filled(i) ? " on" : ""}${pulse(i) ? " pulse" : ""}${
              stage.holding && i === stage.current ? " holdnow" : hold}`} />
          </span>
        ))}
      </span>
      <span className={`aidz-stage${
        stage.done ? (stage.warn ? " warn" : " done") : stage.working ? " working" : ""
      }${hold}`}>
        {stage.label}
      </span>
    </span>
  );
}

/** 점 진행 바 없이 아이콘 + 상태 텍스트만 표시 — 이메일(제목) 줄 우측에 사용.
 *  진행 중이면 회전 스피너, 완료(Done)면 체크 아이콘이 텍스트 왼쪽에 붙는다.
 *  색상 규칙은 StepProgress의 라벨과 동일(working=accent, done=green, warn=amber, err=red). */
function StageLabel({ stage }: { stage: any }) {
  if (stage.error)
    return <span className="aidz-stage err" title={stage.title || ""}>✕ {stage.label}</span>;
  if (stage.blocked)   // 중복(이미 처리됨) — 오류가 아니라 안내: 앰버 경고 아이콘 + 라벨
    return (
      <span className="aidz-stagebox warn" title={stage.title || ""}>
        <WarnIcon /><span className="aidz-stage warn">{stage.label}</span>
      </span>
    );
  if (stage.skipped)
    return <span className="aidz-stage skip">Skipped</span>;

  const cls = stage.done ? (stage.warn ? " warn" : " done") : stage.working ? " working" : "";
  return (
    <span className={`aidz-stagebox${cls}`}>
      {stage.working && !stage.done && <span className="aidz-spin" aria-hidden="true" />}
      {stage.done && <CheckIcon />}
      <span className={`aidz-stage${cls}`}>{stage.label}</span>
    </span>
  );
}

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const WarnIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3 2 20h20L12 3z" /><path d="M12 10v5" /><path d="M12 18h.01" />
  </svg>
);

/**
 * 중복(409) 안내 패널 — 서버 DuplicateResponse를 그린다 (2026-09-22).
 *  - 메시지 · 출처(AI Drop Zone / Email Intake) · 누가 · 언제
 *  - 원본 job의 문서 목록: Completed → [Open ↗] (토스트와 같은 onNavigate 키), Failed → 오류, 미처리 → "Not processed"
 *  - [Resume remaining (N)] (canResume일 때) · [Open AI Center]
 */
function DuplicatePanel({ dup, onNavigate, onResume, onOpenAICenter }:
  { dup: any; onNavigate: any; onResume: () => void; onOpenAICenter: () => void }) {
  const o = dup?.original ?? {};
  const docs: any[] = o.documents ?? [];
  const when = o.createdAt ? new Date(o.createdAt).toLocaleString() : "";
  const source = o.source === "INTAKE" ? "Email Intake" : "AI Drop Zone";

  const open = (e: any, d: any) => {
    e.preventDefault();
    onNavigate?.({
      jobName: d.jobName, jobId: d.jobId, parentJobName: d.parentJobName, parentJobId: d.parentJobId,
      isfType: d.isfType, shipmentId: d.shipmentId,
    });
  };

  return (
    <div className="aidz-dup">
      <div className="aidz-dup-msg">{dup?.message || "This file was already processed."}</div>
      <div className="aidz-dup-meta">
        {source}{o.droppedBy ? ` · ${o.droppedBy}` : ""}{when ? ` · ${when}` : ""}
      </div>
      {docs.length > 0 && (
        <ul className="aidz-dup-docs">
          {docs.map((d: any) => {
            const cat = d.docTypeName || d.docType;
            // recordExists: true=레코드 있음, false=유저가 지움(이어서 처리 대상), null=확인 불가(있음으로 취급)
            const deleted = d.status === "Completed" && d.recordExists === false;
            const linkable = d.status === "Completed" && d.jobId > 0 && !!d.jobName && !deleted;
            return (
              <li key={d.documentId} className={d.status === "Failed" ? "fail" : d.resumable ? "todo" : ""}>
                <span className="aidz-doc-cat" title={cat}>{cat}</span>
                <span className="aidz-doc-file" title={d.filename || ""}>{d.actionNo || d.filename || ""}</span>
                {linkable ? (
                  <a href="#" className="aidz-dup-link" title={d.recordExists === null ? "Record could not be verified" : ""}
                     onClick={(e) => open(e, d)}>Open ↗</a>
                ) : deleted ? (
                  <span className="aidz-stage warn" title="The record created earlier no longer exists - Resume will recreate it">Record deleted</span>
                ) : d.status === "Completed" ? (
                  <span className="aidz-stage done">Done</span>
                ) : d.status === "Failed" ? (
                  <span className="aidz-stage err" title={d.error || ""}>✕ Failed</span>
                ) : d.resumable ? (
                  <span className="aidz-stage warn">Not processed</span>
                ) : (
                  <span className="aidz-stage skip">Skipped</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="aidz-dup-actions">
        {dup?.canResume && (
          <button className="aidz-btn primary" onClick={onResume}>
            Resume remaining{o.resumableDocuments > 0 ? ` (${o.resumableDocuments})` : ""}
          </button>
        )}
        <button className="aidz-btn" onClick={onOpenAICenter}>Open AI Center</button>
      </div>
    </div>
  );
}

const SparkleIcon = () => (
  // <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  //   <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" fill="currentColor" />
  //   <path d="M18.6 15.4l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" fill="currentColor" opacity=".8" />
  // </svg>
  <>
    <img className="aidz-fab-img default" src={aiDropzoneDefault} alt="" />
    <img className="aidz-fab-img hover" src={aiDropzoneHover} alt="" />
  </>
);

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const WORKING_PHASES = ["uploading", "uploaded", "classifying", "classified", "processing", "resuming"];

export default function AiDropZone({ onToast, onNavigate }: { onToast: any; onNavigate?: any }) {
  const [open, setOpen] = useState(false);
  // AI Settings 에서 AI Drop Zone(또는 AI Center 전체)이 꺼져 있으면 아이콘은 보이되 눌러도 열리지 않는다 (2026-10-06)
  const dropZoneOff = isDropZoneOff(useAiCenterFlags());
  useEffect(() => {
    if (dropZoneOff) setOpen(false);
  }, [dropZoneOff]);
  const [drag, setDrag] = useState(false);
  const [hint, setHint] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const history = useHistory();
  const setSavedEntity = useSetEditedId([JobNames.AICenter]);
  const pendingIdsRef = useRef(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setFilterForScreenExternally } = useExternalScreenFilter();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  /**
   * 이미 처리된 레코드로 이동 (2026-09-23 hykim). ISF는 외부 ISF 사이트가 아니라 AI Center의 Review 열 클릭과 같은
   * ISF List 화면(그 레코드로 필터)으로 간다. 나머지(HBL·MBL·Invoice·Bill·CrDb)는 토스트와 같은 onNavigate.
   */
  const openRecord = (entity: any) => {
    if (!entity?.jobId) return;
    if (entity.jobName === JobNames.ISF) {
      const url = "/common/joblistview/ISFL/isf";
      setFilterForScreenExternally(url, "id", "", entity.jobId, "modifyDate", false, true, null,
        "shipmentId", entity.shipmentId ?? "", false, true);
      openNewPage(history, url, NavigationType.newPage);
      return;
    }
    onNavigate?.(entity);
  };

  /** 레코드 토스트 — ISF는 클릭 시 ISF List로 가도록 onClick을 붙인다 */
  const recordToast = (d: any, meta: any, jobKey: any) => {
    const t: any = { ...buildProcessedRecordToast(d, meta), jobKey };
    if (t.entity?.jobName === JobNames.ISF) t.onClick = () => openRecord(t.entity);
    return t;
  };

  const { jobs, addFiles, removeJob, retryJob, resumeJob, subscribeGlobal, unsubscribeGlobal } = useEmailJobStatus({
    onDocumentEvent: (doc: any, raw: any) => {
      if (raw.event === "documentCompleted") {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
          flushTimerRef.current = setTimeout(() => {
          setSavedEntity("0", false, false, true);
          flushTimerRef.current = null; // 실행 후 정리
        }, 200);
         onToast?.({ ...buildActionToast(doc), jobKey: raw.jobId });
      }
    },
    // 업로드가 409(이미 처리됨 / 처리 중)로 막힘 — 살아 있는 레코드마다 앰버 토스트 하나(클릭 → 그 레코드).
    // 레코드가 없는 경우(처리 중·자동 resume)만 요약 토스트 하나. 카드 패널은 훅의 phase 'duplicate'로 따로 그린다
    onDuplicate: (dup: any) => {
      const jobKey = dup?.original?.jobId ?? null;
      const records = dup?.resumed || dup?.inProgress ? [] : processedRecords(dup);
      if (records.length === 0) {
        onToast?.({ ...buildDuplicateToast(dup), jobKey });
        return;
      }
      const meta = { source: dup.original?.source, droppedBy: dup.original?.droppedBy, createdAt: dup.original?.createdAt };
      records.forEach((d: any) => onToast?.(recordToast(d, meta, jobKey)));
      // 이어서 처리 안내는 토스트로 띄우지 않는다 — 카드의 [Resume remaining] 버튼으로 충분 (2026-09-23 hykim)
    },
    // 첨부 일부만 이미 처리됨(Duplicate 파일 행) — 그 첨부가 만든 레코드마다 토스트 하나
    onSkippedAttachment: (file: any, jobId: string | null) => {
      const meta = { createdAt: file?.duplicateOf?.processedAt ?? null };
      const records = (file?.duplicateOf?.documents ?? []).filter(
        (d: any) => d.status === "Completed" && d.jobId > 0 && d.jobName && d.recordExists !== false);
      records.forEach((d: any) => onToast?.(recordToast(d, meta, jobId)));
    },
  });

  useEffect(() => {
    // 개별 job 추적과 무관하게 tenant 전체 customMessage(toast)를 받기 위한 구독
    const asyncfunc = async() => {
      try {
        await subscribeGlobal();
      } catch (err) {
        console.error("Failed to subscribe to job updates:", err);
      }
    };
    asyncfunc(); // 호출 추가
    return () => {
      unsubscribeGlobal();
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  const workingCount = jobs.filter((j: any) => WORKING_PHASES.includes(j.phase) && !j.leaving).length;
  // 배지 "!" — 오류뿐 아니라 중복으로 막힌 항목도 사람이 봐야 한다
  const hasError = jobs.some((j: any) => ["error", "failed", "doneWithErrors", "duplicate"].includes(j.phase));

  const showHint = (text: string) => {
    setHint(text);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);  // 정리는 조건부
    hintTimerRef.current = setTimeout(() => setHint(""), 3000);    // 등록은 무조건
  };

  const acceptFiles = (fileList: any) => {
    const accepted = addFiles(fileList);
    //if (accepted === 0) showHint("Only .eml / .msg files are supported.");
    if (accepted === 0) showHint("Not supported file type");
  };

  const onDrop = (e: any) => {
    e.preventDefault();
    setDrag(false);
    acceptFiles(e.dataTransfer.files);
  };

  function openAICenter() {
    const url = '/common/joblistview/AICTR/aiCenter';
    if (url) {
      openNewPage(history, url, NavigationType.newPage);
    }
  }


  function FileTypeIcon({ fileName }: { fileName: string }) {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();

  switch (ext) {
    case "eml":
    case "msg":
      return <MailIcon />;
    case "jpg":
    case "jpeg":
    case "png":
    case "tif":
    case "tiff":
      return <img src={pngImg} width={15} height={15} alt="" />;
    case "pdf":
      return <img src={pdfImg} width={15} height={15} alt="" />;
    case "xls":
    case "xlsx":
      return <img src={excelImg} width={15} height={15} alt="" />;
    case "doc":
    case "docx":
      return <img src={wordImg} width={15} height={15} alt="" />;
    default:
      return <MailIcon />;
  }
}

  return (
    <div className="aidz">
      <style>{STYLE}</style>

      {open && (
        <div className="aidz-card"
             onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
             onDragLeave={(e: any) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
             onDrop={onDrop}>
          <div className="aidz-head">
            <div>
              <div className="aidz-title">AI Drop Zone</div>
              {/* <div className="aidz-sub">Drop email files to process them automatically</div> */}
            </div>
            <button className="aidz-x" aria-label="close" onClick={() => setOpen(false)}>×</button>
          </div>

          <div className={`aidz-zone${drag ? " drag" : ""}`}
               onClick={() => inputRef.current?.click()}>
            <div className="aidz-zone-ic">⬆</div>
            <div className="aidz-zone-t">Drop .eml / .msg files here</div>
            <div className="aidz-zone-s">or click to browse</div>
            <input ref={inputRef} type="file" accept=".eml,.msg" multiple hidden
                   onChange={(e) => { acceptFiles(e.target.files); e.target.value = ""; }} />
          </div>
          {hint && <div className="aidz-hint">{hint}</div>}

          <div className="aidz-list">
            {/* {jobs.length === 0 && (
              <div className="aidz-empty">No emails in progress yet.</div>
            )} */}
            {jobs.map((j: any) => {
              // Unknown 클래스 문서는 카드에 표시하지 않는다 (백엔드에서도 Skipped로 제외됨)
              const visibleDocs = j.docs.filter(
                (d: any) => !/^unknown$/i.test((d.docTypeName || d.docType || "").trim()));
              return (
              <div key={j.key} className={`aidz-job${j.leaving ? " leaving" : ""}`}>
                <div className="aidz-job-head">
                  <span className="aidz-job-ic"><FileTypeIcon fileName={j.fileName} /></span>
                  <span className="aidz-job-name" title={j.fileName}>{j.fileName}</span>
                  {/* 이메일 줄은 진행 바 없이 스피너/체크 + 상태 텍스트만 */}
                  <StageLabel stage={emailStage(j)} />
                  <button className="aidz-x sm" aria-label="remove"
                          onClick={() => removeJob(j.key)}>×</button>
                </div>

                {(j.phase === "error" || j.phase === "failed") && j.message && (
                  <div className="aidz-err">
                    {j.message}
                    {/* {j.phase === "error" && j.jobId && (
                      <button className="aidz-retry" onClick={() => retryJob(j.key)}>Retry</button>
                    )} */}
                  </div>
                )}

                {/* 중복(이미 처리됨) — 안내 + 생성된 데이터 링크 + 남은 문서 이어서 처리 */}
                {j.phase === "duplicate" && (
                  <DuplicatePanel
                    dup={{ ...(j.duplicate ?? {}), message: j.message || j.duplicate?.message }}
                    onNavigate={openRecord}
                    onResume={() => resumeJob(j.key, j.jobId)}
                    onOpenAICenter={openAICenter}
                  />
                )}

                {/* 서버가 이전 job을 자동으로 이어서 처리한 경우 — 왜 이 job을 보고 있는지 한 줄 */}
                {j.resumed && j.message && (
                  <div className="aidz-note">{j.message}</div>
                )}

                {/* 이미 처리된 첨부 — 문서 없이 건너뛴 파일 줄 (이전 레코드 링크) */}
                {(j.skippedFiles?.length ?? 0) > 0 && (
                  <ul className="aidz-docs">
                    {j.skippedFiles.map((f: any) => {
                      const first = (f.duplicateOf?.documents ?? []).find(
                        (d: any) => d.status === "Completed" && d.jobId > 0 && d.jobName && d.recordExists !== false);
                      return (
                        <li key={f.fileId} className="skip" title={f.error || "This attachment was already processed earlier"}>
                          <span className="aidz-doc-cat">Already processed</span>
                          <span className="aidz-doc-file" title={f.filename}>{f.filename}</span>
                          {first ? (
                            <a href="#" className="aidz-dup-link" onClick={(e) => {
                              e.preventDefault();
                              openRecord({
                                jobName: first.jobName, jobId: first.jobId, parentJobName: first.parentJobName,
                                parentJobId: first.parentJobId, isfType: first.isfType, shipmentId: first.shipmentId,
                              });
                            }}>Open ↗</a>
                          ) : (
                            <span className="aidz-stage skip">Skipped</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {visibleDocs.length > 0 && (
                  <ul className="aidz-docs">
                    {visibleDocs.map((d: any) => {
                      const base = docStage(d);
                      const st = isHeldByDependency(d)
                        ? { ...base, holding: true, label: "Holding" }
                        : base;
                      return (
                        <li key={d.documentId} className={st.skipped ? "skip" : ""}>
                          <span className="aidz-doc-cat" title={d.docTypeName || d.docType}>
                            {d.docTypeName || d.docType}
                          </span>
                          <span className="aidz-doc-file" title={d.filename}>{d.filename}</span>
                          <StepProgress steps={DOC_STEPS} stage={st} />
                        </li>
                      );
                    })}
                  </ul>
                )}

                {j.phase === "done" && (
                  <div className="aidz-done">
                    <span className="aidz-done-ic">✓</span> All tasks for this email are complete.
                  </div>
                )}
                {j.phase === "doneWithErrors" && (
                  <div className="aidz-done warn">
                    ⚠ Finished, but some documents failed.
                  </div>
                )}
              </div>
              );
            })}
            </div>
            <div className="ai-dropzone-panel__footer">
              <a
                href="#"
                className="ai-center-row__link"
                onClick={(e) => {
                  e.preventDefault();
                 openAICenter();
                }}
              >
                Open AI Center ↗
              </a>
          </div>
        </div>
      )}

      <button className="aidz-fab" aria-label="AI Drop Zone"
        onClick={() => {
          if (dropZoneOff) {
            showGlobalOkDialog(<p>{AI_CENTER_CONTACT_SUPPORT}</p>, false, null, 'warning');
            return;
          }
          setOpen((v) => !v);
        }}
        onDragEnter={() => { if (!dropZoneOff) setOpen(true); }}>
        <SparkleIcon />
        {workingCount > 0 && <span className="aidz-badge">{workingCount}</span>}
        {workingCount === 0 && hasError && <span className="aidz-badge err">!</span>}
      </button>
    </div>
  );
}

/* my-design-system 토큰(라이트 · 블루 액센트) 기반 스코프 스타일 */
const STYLE = `
.aidz {
  --bg:#FFFFFF; --bg-subtle:#F8FAFC; --surface:#FFFFFF;
  --border:#E2E8F0; --border-strong:#CBD5E1;
  --text:#0F172A; --text-2:#475569; --text-3:#94A3B8;
  --accent:#2563EB; --accent-hover:#1D4ED8; --accent-subtle:#EFF6FF;
  --accent-soft:#93C5FD;
  --ring:rgba(37,99,235,.35);
  --success:#16A34A; --success-subtle:#F0FDF4;
  --warning:#D97706; --warning-subtle:#FFFBEB;
  --warning-soft:#FCD34D; --warning-bright:#FBBF24;
  --danger:#DC2626;  --danger-subtle:#FEF2F2;
  position:fixed; right:24px; bottom:24px; z-index:900;
  font-family:"Pretendard Variable",Pretendard,-apple-system,"Segoe UI","Noto Sans KR",sans-serif;
  color:var(--text);
  z-index: 10000;
}
.aidz *, .aidz *::before, .aidz *::after { box-sizing:border-box; }

.ai-dropzone-panel__footer {
  padding: 6px 14px;
  border-top: 1px solid #dcdcdc;
  text-align: center;
}
.ai-center-row__link {
  color: #2f6fed;
  text-decoration: none;
  font-size: 12px;
}
.ai-center-row__link:hover { text-decoration: underline; }

/* ── 플로팅 AI 버튼 ── */
.aidz-fab {
  position:relative; width:44px; height:44px; border-radius:50%;
  border:none; cursor:pointer; background: transparent;
  display:flex; align-items:center; justify-content:center;
  transition:transform .18s ease-out;
  z-index: 10000;
}
.aidz-fab:hover { background:transparent; transform:scale(1.05);}
.aidz-fab:focus { outline: none; }

.aidz-fab-img.hover { display: none; }
.aidz-fab:hover .aidz-fab-img.default { display: none; }
.aidz-fab:hover .aidz-fab-img.hover   { display: block; }

.aidz-badge {
  position:absolute; top:-4px; right:-4px; min-width:18px; height:18px;
  padding:0 5px; border-radius:9px; background:#fff; color:var(--accent);
  border:1px solid var(--border); font-size:11px; font-weight:600;
  display:flex; align-items:center; justify-content:center;
}
.aidz-badge.err { color:var(--danger); }

/* ── 카드 ── */
.aidz-card {
  position:absolute; right:0; bottom:64px; width:372px; max-width:calc(100vw - 32px);
  max-height:min(560px, calc(100vh - 140px));
  display:flex; flex-direction:column;
  background:linear-gradient(180deg, #FFFFFF 0%, #EEF3FC 100%); border:1px solid var(--border-strong); border-radius:12px;
  box-shadow: 0 16px 40px rgb(15 23 42 / .18);
  transform-origin:bottom right;
  animation:aidz-pop .2s ease-out;
  overflow: hidden;   
}
@keyframes aidz-pop {
  from { opacity:0; transform:translateY(8px) scale(.97); }
  to   { opacity:1; transform:translateY(0)  scale(1); }
}
.aidz-head { display:flex; align-items:flex-start; justify-content:space-between;
  padding:16px 16px 12px; }
.aidz-title { font-size:14px; font-weight:600; }
.aidz-sub { font-size:12px; color:var(--text-2); margin-top:2px; }
.aidz-x {
  border:none; background:transparent; cursor:pointer; color:var(--text-3);
  font-size:18px; line-height:1; padding:2px 4px; border-radius:6px;
  transition:color .15s ease-out, background .15s ease-out;
}
.aidz-x:hover { color:var(--text); background:var(--bg-subtle); }
.aidz-x.sm { font-size:15px; }

/* ── 드롭 존 ── */
.aidz-zone {
  margin:0 16px; padding:18px 12px; text-align:center; cursor:pointer;
  border:1.5px dashed var(--border-strong); border-radius:8px;
  transition:border-color .15s ease-out, background .15s ease-out;
}
.aidz-zone:hover { border-color:var(--accent); }
.aidz-zone.drag { border-color:var(--accent); background:var(--accent-subtle); }
.aidz-zone-ic { font-size:18px; color:var(--accent); }
.aidz-zone-t { font-size:13px; font-weight:500; margin-top:4px; }
.aidz-zone-s { font-size:11px; color:var(--text-3); margin-top:2px; }
.aidz-hint { margin:8px 16px 0; font-size:12px; color:var(--warning); }

/* ── job 목록 ── */
.aidz-list { overflow-y:auto; padding:8px 16px 14px; }
.aidz-empty { padding:14px 0 6px; font-size:12px; color:var(--text-3); text-align:center; }
.aidz-job {
  padding:10px 0; border-top:1px solid var(--border);
  animation:aidz-item .2s ease-out;
  transition:opacity .2s ease-out, transform .2s ease-out;
}
.aidz-job:first-child { border-top:none; }
.aidz-job.leaving { opacity:0; transform:translateY(6px); }
@keyframes aidz-item { from { opacity:0; transform:translateY(6px); } to { opacity:1; } }

.aidz-job-head { display:flex; align-items:center; gap:8px; min-width:0; }
.aidz-job-ic { color:var(--text-2); display:flex; flex:none; }
.aidz-job-name {
  flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  font-size:13px; font-weight:500;
}
.aidz-err {
  margin:6px 0 0 23px; font-size:12px; color:var(--danger);
  background:var(--danger-subtle); border-radius:6px; padding:6px 8px;
}
.aidz-retry {
  margin-left:8px; border:1px solid var(--border-strong); background:var(--surface);
  border-radius:6px; padding:1px 8px; font-size:11px; cursor:pointer; color:var(--text);
}

/* 문서(하위) 목록 — 들여쓰기 */
.aidz-docs { list-style:none; margin:6px 0 0; padding:0 0 0 23px; }
.aidz-docs li {
  display:flex; align-items:center; gap:6px; min-width:0; padding:3px 0;
  animation:aidz-item .2s ease-out;
}
.aidz-docs li.skip { opacity:.55; }
.aidz-doc-cat { flex:none; max-width:38%; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap; font-size:12px; font-weight:500; }
.aidz-doc-file { flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap; font-size:11px; color:var(--text-3); }

/* ── 단계 progress (점 + 현재 단계 라벨) ── */
.aidz-progress { flex:none; display:inline-flex; align-items:center; gap:6px; }
.aidz-dots { display:inline-flex; align-items:center; }
.aidz-seg { display:inline-flex; align-items:center; }

.aidz-line { width:12px; height:2px; border-radius:1px; background:var(--border);
  transition:background .15s ease-out; }
.aidz-line.on,
.aidz-line.active { background:var(--accent-soft); }

.aidz-dot {
  position:relative; width:8px; height:8px; border-radius:50%;
  border:2px solid var(--border-strong); background:var(--surface);
  transition:background .15s ease-out, border-color .15s ease-out, transform .15s ease-out;
}
.aidz-dot.on { background:var(--accent-soft); border-color:var(--accent-soft); }

/* 현재 진행 중 — 크기 + 채도 + 헤일로 + 확산 링 */
.aidz-dot.pulse {
  background:var(--accent); border-color:var(--accent);
  transform:scale(1.4); z-index:1;
  box-shadow:0 0 0 3px var(--accent-subtle);
  animation:aidz-breathe 1.6s ease-in-out infinite;
}
.aidz-dot.pulse::after {
  content:""; position:absolute; inset:-2px; border-radius:50%;
  border:2px solid var(--accent);
  animation:aidz-ring 1.6s ease-out infinite;
}
@keyframes aidz-breathe {
  0%,100% { transform:scale(1.3); }
  50%     { transform:scale(1.5); }
}
@keyframes aidz-ring {
  0%       { transform:scale(.9); opacity:.5; }
  70%,100% { transform:scale(2);  opacity:0; }
}

/* 트랙이 끝나면 지난 점들도 원래 accent 로 복귀 */
.aidz-progress.is-done .aidz-dot.on { background:var(--accent); border-color:var(--accent); }
.aidz-progress.is-done .aidz-line.on { background:var(--accent); }

/* ── 의존성 대기(Holding) — 앰버 톤 + 그라데이션 shimmer ──
 * 같은 시각 위계를 앰버로 옮긴다:
 *   지난 단계    = 연한 앰버 점(warning-soft)
 *   현재(Parsed) = 앰버 그라데이션 shimmer 점 + 확대 + 앰버 헤일로 (확산 링 없음 — "대기"니까)
 *   연결선·라벨  = 그라데이션이 좌우로 흐르는 shimmer                         */
.aidz-dot.on.hold { background:var(--warning-soft); border-color:var(--warning-soft); }
.aidz-line.on.hold {
  background:linear-gradient(90deg,var(--warning),var(--warning-bright),var(--warning));
  background-size:200% 100%;
  animation:aidz-shimmer 1.6s linear infinite;
}
.aidz-dot.holdnow {
  border-color:transparent; z-index:1;
  transform:scale(1.25);
  background:linear-gradient(90deg,var(--warning),var(--warning-bright),var(--warning));
  background-size:200% 100%;
  box-shadow:0 0 0 3px var(--warning-subtle);
  animation:aidz-shimmer 1.6s linear infinite;
}
.aidz-stage.hold {
  background:linear-gradient(90deg,var(--warning),var(--warning-bright) 50%,var(--warning));
  background-size:200% 100%;
  -webkit-background-clip:text; background-clip:text; color:transparent;
  animation:aidz-shimmer 1.6s linear infinite;
}
@keyframes aidz-shimmer {
  from { background-position:200% 0; }
  to   { background-position:-200% 0; }
}

.aidz-stage { flex:none; min-width:58px; text-align:right; white-space:nowrap;
  font-size:11px; font-weight:500; color:var(--text-2); letter-spacing:.02em; }
.aidz-stage.working { color:var(--accent); }
.aidz-stage.done { color:var(--success); }
.aidz-stage.warn { color:var(--warning); }
.aidz-stage.err  { color:var(--danger); }
.aidz-stage.skip { color:var(--text-3); }

/* 이메일 줄 상태 표시 — 아이콘(스피너/체크) + 텍스트 */
.aidz-stagebox { display:inline-flex; align-items:center; gap:5px; flex:none; }
.aidz-stagebox.done { color:var(--success); }
.aidz-stagebox.warn { color:var(--warning); }
.aidz-stagebox .aidz-stage { min-width:0; }
.aidz-spin {
  flex:none; width:12px; height:12px; border-radius:50%;
  border:2px solid var(--accent-subtle);
  border-top-color:var(--accent);
  animation:aidz-rotate .8s linear infinite;
}
@keyframes aidz-rotate { to { transform:rotate(360deg); } }

/* ── 중복(이미 처리됨) 안내 패널 ── */
.aidz-dup {
  margin:8px 0 0 23px; padding:8px 10px; border-radius:6px;
  background:var(--warning-subtle); border:1px solid #FDE68A;
  animation:aidz-item .2s ease-out;
}
.aidz-dup-msg { font-size:12px; font-weight:500; color:var(--warning); line-height:1.4; }
.aidz-note { margin:6px 0 0 23px; font-size:11px; color:var(--warning); line-height:1.4; }
.aidz-dup-meta { font-size:11px; color:var(--text-3); margin-top:2px; }
.aidz-dup-docs { list-style:none; margin:6px 0 0; padding:0; }
.aidz-dup-docs li { display:flex; align-items:center; gap:6px; min-width:0; padding:3px 0; }
.aidz-dup-docs li.todo .aidz-doc-cat { color:var(--warning); }
.aidz-dup-link { flex:none; font-size:11px; font-weight:600; color:var(--accent); text-decoration:none; }
.aidz-dup-link:hover { text-decoration:underline; }
.aidz-dup-actions { display:flex; gap:6px; margin-top:8px; }
.aidz-btn {
  border:1px solid var(--border-strong); background:var(--surface); color:var(--text);
  border-radius:6px; padding:3px 10px; font-size:11px; font-weight:500; cursor:pointer;
  transition:background .15s ease-out, border-color .15s ease-out;
}
.aidz-btn:hover { background:var(--bg-subtle); }
.aidz-btn.primary { background:var(--accent); border-color:var(--accent); color:#fff; }
.aidz-btn.primary:hover { background:var(--accent-hover); }

/* ── 전체 완료 스트립 ── */
.aidz-done {
  display:flex; align-items:center; gap:6px;
  margin:8px 0 0 23px; padding:7px 10px; border-radius:6px;
  font-size:12px; font-weight:500;
  color:var(--success); background:var(--success-subtle);
  animation:aidz-item .2s ease-out;
}
.aidz-done.warn { color:var(--warning); background:var(--warning-subtle); }
.aidz-done-ic { font-weight:700; }

@media (prefers-reduced-motion: reduce) {
  .aidz-card, .aidz-job, .aidz-docs li, .aidz-done { animation:none; }
  .aidz-dot.pulse { animation:none; transform:scale(1.4);
    box-shadow:0 0 0 3px var(--accent-subtle), 0 0 0 4px var(--ring); }
  .aidz-dot.pulse::after { animation:none; transform:scale(1.5); opacity:.35; }
  .aidz-dot.holdnow, .aidz-line.on.hold, .aidz-stage.hold { animation:none; }
  .aidz-spin { animation:none; }
  .aidz-dot.holdnow { background:var(--warning); transform:scale(1.25);
    box-shadow:0 0 0 3px var(--warning-subtle); }
  .aidz-line.on.hold { background:var(--warning-soft); }
  .aidz-stage.hold { background:none; color:var(--warning); }
  .aidz-fab, .aidz-job { transition:none; }
}
`;
